import React from "react";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import {
  getProductsData,
  getSolutionsData,
  getArticlesData,
  getArticlesCategories,
  getHelpCenterData,
  getLegalPagesData,
  getPostById,
  getPostsByCategory,
  searchPosts,
  getPostBySlug,
  getRelatedPosts,
  getPlansData,
  getHelpCenterCategoriesData,
  getArticlesCategoriesDataById,
  getHelpCenterCategoryDataById,
  getHelpCenterCategoryById,
  getFeaturesData,
  getStaticVsDynamicData,
  getFAQData,
} from "@/lib/wordpressApi";

// Custom hook for WordPress API with automatic language detection
export const useWordPressApi = () => {
  const router = useRouter();
  const { i18n } = useTranslation();

  // Get current language
  const currentLanguage =
    (router.query.lang as string) || i18n.language || "en";

  return {
    // Language info
    currentLanguage,

    // API functions with automatic language detection
    getProducts: () => getProductsData(currentLanguage),
    getSolutions: () => getSolutionsData(currentLanguage),
    getArticles: () => getArticlesData(currentLanguage),
    getArticlesCategories: () => getArticlesCategories(currentLanguage),
    getHelpCenter: () => getHelpCenterData(currentLanguage),
    getHelpCenterCategories: () => getHelpCenterCategoriesData(currentLanguage),
    getArticlesCategoriesById: (categoryId: number, currentLanguage) =>
      getArticlesCategoriesDataById(categoryId, currentLanguage),
    getHelpCenterCategoryDataById: (categoryId: number, currentLanguage) =>
      getHelpCenterCategoryDataById(categoryId, currentLanguage),
    getHelpCenterCategoryById: (categoryId: number) =>
      getHelpCenterCategoryById(categoryId, currentLanguage),
    getLegalPages: () => getLegalPagesData(currentLanguage),
    getPlansData: () => getPlansData(),

    // Specific post functions
    getProductById: (id: number) => getPostById(id, currentLanguage),
    getSolutionById: (id: number) =>
      getPostById(id, "solutions", currentLanguage),
    getArticleById: (id: number) =>
      getPostById(id, "articles", currentLanguage),
    getArticleBySlug: (slug: string) =>
      getPostBySlug(slug, "articles", currentLanguage),
    getProductBySlug: (slug: string) =>
      getPostBySlug(slug, "products", currentLanguage),
    getLegalPagesDataById: (id: number) =>
      getPostById(id, "legalPages", currentLanguage),
    getHelpCenterById: (id: number, currentLanguage) =>
      getPostById(id, "help_center", currentLanguage),

    // Category and search functions
    getPostsByCategory: (categoryId: number, postType: string = "articles") =>
      getPostsByCategory(categoryId, postType, currentLanguage),
    searchPosts: (searchTerm: string, postType: string = "articles") =>
      searchPosts(searchTerm, postType, currentLanguage),
    getPostBySlug: (slug: string, postType: string = "products") =>
      getPostBySlug(slug, postType, currentLanguage),
    getRelatedPosts: (
      postId: number,
      categoryId: number,
      postType: string = "articles"
    ) => getRelatedPosts(postId, categoryId, postType, currentLanguage),
    getFeaturesData: () => getFeaturesData(currentLanguage),
    getStaticVsDynamicData: () => getStaticVsDynamicData(currentLanguage),
    getFAQData: () => getFAQData(currentLanguage),
  };
};

// Hook for fetching data with loading states
export const useWordPressData = <T>(
  fetchFunction: () => Promise<T>,
  dependencies: any[] = []
) => {
  const [data, setData] = React.useState<T | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await fetchFunction();
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
        console.error("WordPress API Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, dependencies);

  return { data, loading, error };
};
